<template>
  <div class="columns">
    <div class="column is-6">
      <form novalidate @submit.prevent="syncOrder">
        <!-- gate section -->
        <LsRadioButtons
          v-model="property_info.gate.has_gate"
          :validation="{ required: true }"
          class="onboarding-property-info-view--section"
          label="Do you have a gate?"
          :options="options"
          name="gate.has_gate"
          track-id="onboarding_property_info_view-gate_has_gate"
          test-id="gate_has_gate"
          @input="onGateChange"
        />
        <BCollapse :open="property_info.gate.has_gate">
          <LockGateInput
            v-model="property_info.gate.code"
            track-id="onboarding_property_info_view-gate_code"
            class="onboarding-property-info-view--section"
            :form-dirty="formDirty"
            @input="onLockGateChange"
          />
          <WideGateInput
            v-model="property_info.gate.width"
            :form-dirty="formDirty"
            class="onboarding-property-info-view--section"
          />
        </BCollapse>

        <!-- multitenant section -->
        <LsRadioButtons
          v-model="property_info.multitenant.is_multitenant"
          :validation="{ required: true }"
          class="onboarding-property-info-view--section"
          label="Is the property a duplex, townhome, or other type of multi-family dwelling?"
          :options="options"
          name="multitenant.is_multitenant"
          track-id="onboarding_property_info_view-multitenant_is_multitenant"
          test-id="multitenant_is_multitenant"
          @input="onMultitenantChange"
        />
        <BCollapse :open="property_info.multitenant.is_multitenant">
          <LsInput
            v-model="property_info.multitenant.units[0]"
            :validation="{
              required: Boolean(property_info.multitenant.is_multitenant),
            }"
            class="onboarding-property-info-view--section"
            label="Please add the unit(s) numbers/letters that are to receive service:"
            test-id="multitenant_units"
            name="multitenant.units"
            track-id="onboarding_property_info_view-multitenant_units"
          />
        </BCollapse>

        <!-- scope section -->
        <div
          v-if="!isStandAloneTreatment"
          testid="scopeService"
          class="onboarding-property-info-view--section"
        >
          <LsRadios
            v-model="property_info.mowing_preferences.scope"
            :validation="{ required: true }"
            label="Do you require regular lawn maintenance on the whole property or only part of the property (e.g. front-yard only)?"
            :options="[
              { label: 'Entire property', value: 'all' },
              { label: 'Front yard only', value: 'frontonly' },
              { label: 'Back yard only', value: 'backonly' },
            ]"
            name="mowing_preferences.scope"
            track-id="onboarding_property_info_view-mowing_preferences_scope"
          />
          <BCollapse
            :open="
              property_info.mowing_preferences.scope === 'all' &&
                propertyInfo.mowing_preferences.scope !== 'all'
            "
          >
            <LsInfoCard class="onboarding-property-info-view--scope">
              <p class="is-italic">
                Full property mows will increase your per service price by 25%. This allows us to
                properly compensate our crews for the additional work at your property.
              </p>
            </LsInfoCard>
          </BCollapse>
          <BCollapse
            :open="
              property_info.mowing_preferences.scope !== 'all' &&
                propertyInfo.mowing_preferences.scope === 'all'
            "
          >
            <LsInfoCard class="onboarding-property-info-view--scope">
              <p class="is-italic">
                Partial property mows will reduce your per service price by 20%. This allows us to
                properly compensate our crews for time and travel to your property.
              </p>
            </LsInfoCard>
          </BCollapse>
        </div>

        <!-- community section -->
        <LsRadioButtons
          v-model="property_info.community.restricted"
          :validation="{ required: true }"
          class="onboarding-property-info-view--section"
          label="Is access to your neighborhood restricted (e.g. gates)?"
          :options="options"
          name="community.restricted"
          track-id="onboarding_property_info_view-community_restricted"
          test-id="community_restricted"
          @input="onCommunityChange"
        />
        <BCollapse :open="property_info.community.restricted">
          <LsRadioButtons
            v-model="property_info.community.has_code"
            :validation="{ required: Boolean(property_info.community.restricted) }"
            class="onboarding-property-info-view--section"
            type="textarea"
            label="Do you have an access code?"
            name="community.has_code"
            :options="options"
            track-id="onboarding_property_info_view-community_has_code"
            test-id="community_has_code"
            @input="onCommunityHasCodeChange"
          />
          <BCollapse :open="property_info.community.has_code">
            <LsInput
              v-model="property_info.community.code"
              :validation="{ required: Boolean(property_info.community.has_code) }"
              class="onboarding-property-info-view--section"
              label="What is the access code?"
              test-id="community_code"
              name="community.code"
              track-id="onboarding_property_info_view-community_code"
            />
          </BCollapse>
          <LsInput
            v-model="property_info.community.instructions"
            :validation="{ required: Boolean(property_info.community.restricted) }"
            class="onboarding-property-info-view--section"
            type="textarea"
            label="Are there any instructions needed for the crew to access your community?"
            name="community.instructions"
            track-id="onboarding_property_info_view-community_instructions"
          />
        </BCollapse>

        <LsRadios
          v-if="!isStandAloneTreatment"
          v-model="property_info.grasslength"
          testid="grassHeight"
          :validation="{ required: true }"
          :label="t('grassHeight.question')"
          :options="grassHeightOptions"
          name="grasslength"
          class="onboarding-property-info-view--section"
          track-id="onboarding_property_required_info_view-grasslength"
        />

        <LsButton
          text="SAVE &amp; CONTINUE"
          track-id="onboarding_property_info_view-save_and_continue"
          :loading="onboarding.loadingStatus.isUpdating"
          type="submit"
        />
      </form>
    </div>
  </div>
</template>

<script>
import { LockGateInput, WideGateInput } from '@/components';
import { LsRadioButtons, LsInput, LsButton, LsRadios, LsInfoCard } from '@/components/common';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  AUTH__PROCESSING_ORDER,
  FEATURE_FLAGS__GET_FLAGS,
  ONBOARDING__SELECTED_SERVICE,
  ONBOARDING__SYNC_ORDER,
  ONBOARDING__UPDATE_PRICED_LTRS_SERVICE,
} from '@/store/types';
import _ from 'lodash';
import { lsValidationService } from 'ls-vue-common';
import { lsOrderServicesHelpers, serviceType, lsLongGrassHelpers } from '@/helpers';

export default {
  name: 'OnboardingPropertyInfoView',
  components: {
    WideGateInput,
    LockGateInput,
    LsRadioButtons,
    LsInput,
    LsButton,
    LsRadios,
    LsInfoCard,
  },
  $_veeValidate: {
    validator: 'new',
  },
  data() {
    return {
      property_info: {
        gate: {
          has_gate: null,
          locked: null,
          code: null,
          width: '',
        },
        multitenant: {
          is_multitenant: null,
          units: [''],
        },
        community: {
          restricted: null,
          has_code: null,
          code: '',
          instructions: '',
        },
        mowing_preferences: {
          scope: 'all',
        },
        grasslength: null,
      },
      formDirty: false,
      start_date: '',
    };
  },
  computed: {
    ...mapGetters([AUTH__PROCESSING_ORDER, ONBOARDING__SELECTED_SERVICE]),
    ...mapState(['onboarding', 'featureFlags']),
    propertyInfo() {
      return this[AUTH__PROCESSING_ORDER].property_info;
    },
    isStandAloneTreatment() {
      const intent = this[AUTH__PROCESSING_ORDER].type;
      return intent === serviceType.TREATMENT;
    },
    ltrsService() {
      return this[ONBOARDING__SELECTED_SERVICE]('fertilization');
    },
    options() {
      return [{ label: 'No', value: false }, { label: 'Yes', value: true }];
    },
    customComponentsAreInvalid() {
      const { gate } = this.property_info;

      if (gate.has_gate) {
        if (_.isNull(gate.locked) || (gate.locked && gate.code === '') || !gate.width) {
          return true;
        }
      }
      return false;
    },
    grassHeightOptions() {
      return lsLongGrassHelpers.getGrassLengthOptions();
    },
  },
  created() {
    const { gate, multitenant, community, mowing_preferences, grasslength } = this.propertyInfo;
    this.property_info = {
      gate: {
        has_gate: gate.has_gate,
        locked: gate.locked,
        code: gate.locked === false ? false : gate.code,
        width: _.isNull(gate.width) ? '' : gate.width,
      },
      multitenant: {
        is_multitenant: multitenant.is_multitenant,
        units: _.isEmpty(multitenant.units[0]) ? [''] : [_.toString(multitenant.units)],
      },
      community: {
        restricted: community.restricted,
        has_code: community.has_code,
        code: _.isNull(community.code) ? '' : community.code,
        instructions: _.isNull(community.instructions) ? '' : community.instructions,
      },
      mowing_preferences: {
        scope: mowing_preferences.scope,
      },
      grasslength,
    };
  },
  methods: {
    ...mapActions([
      FEATURE_FLAGS__GET_FLAGS,
      ONBOARDING__SYNC_ORDER,
      ONBOARDING__UPDATE_PRICED_LTRS_SERVICE,
    ]),
    async syncOrder() {
      const valid = await this.$validator.validateAll();
      this.formDirty = true;
      if (!valid || this.customComponentsAreInvalid) {
        return lsValidationService.scrollToInvalid();
      }

      if (this.property_info.gate.code === false) {
        this.property_info.gate.code = '';
      }

      const ltrsService = this.ltrsService;
      if (ltrsService && ltrsService.orderservice_id) {
        const dateOptions = lsOrderServicesHelpers.generateLtrsStartDateOptions();
        // Do not route to next step, since next action below ONBOARDING__SYNC_ORDER
        // will already go to next step.
        this[ONBOARDING__UPDATE_PRICED_LTRS_SERVICE]({
          goToNextStep: false,
          start_date: dateOptions[0].value,
          selectedService: ltrsService,
        });
      }

      this[ONBOARDING__SYNC_ORDER]({
        property_info: this.property_info,
        distribute_early: this.isStandAloneTreatment ? true : null,
      });
    },
    onGateChange() {
      if (this.property_info.gate.has_gate === false) {
        this.property_info.gate.locked = null;
        this.property_info.gate.code = null;
        this.property_info.gate.width = '';
      }
    },
    onCommunityChange() {
      if (this.property_info.community.restricted === false) {
        this.property_info.community.has_code = null;
        this.property_info.community.code = '';
        this.property_info.community.instructions = '';
      }
    },
    onCommunityHasCodeChange() {
      if (this.property_info.community.has_code === false) {
        this.property_info.community.code = '';
      }
    },
    onMultitenantChange() {
      if (this.property_info.multitenant.is_multitenant === false) {
        this.property_info.multitenant.units[0] = '';
      }
    },
    onLockGateChange(value) {
      this.property_info.gate = {
        ...this.property_info.gate,
        ...value,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.onboarding-property-info-view--section {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid $primary-light;
}

.onboarding-property-info-view--scope {
  @media (max-width: $mobile-max-width) {
    margin-top: 30px;
  }
}
.themed-app {
  .onboarding-property-info-view--section {
    border-color: $theme-color-primary-light;
  }
}
</style>
